import { Button, Form, Space } from "antd";
import { useEffect, useState } from "react";
import WarningIcon from "../../assets/icons/WarningIcon";
import { mailRegex } from "../../constants/constants";
import { useSendInviteMutation } from "../../features/employee/employeeApi";
import useError from "../../hooks/useError";
import useModal from "../../hooks/useModal";
import { ModalComponentProps } from "../../types";
import { isValidTLD } from "../../utils/utils";
import CustomModal from "../ui/CustomModal";
import ExistEmailModal from "./ExistEmailModal";
import InviteSuccessModal from "./InviteSuccessModal";

type ModalType = "SUCCESS" | "EXIST";

export default function InviteEmployeeModal({
  handleClose,
  ...rest
}: ModalComponentProps) {
  const [form] = Form.useForm();
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
  const {
    open,
    handleOpen,
    handleClose: handleChildClose,
  } = useModal<string[], ModalType>();
  const [sendInvite, { isLoading, isError, error, isSuccess }] =
    useSendInviteMutation();
  useError(isError, error, "Fail to send invite");

  const onFinish = () => {
    sendInvite({
      email: emails,
    });
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    try {
      await form.validateFields();

      if (e.key === "Enter" || e.key === "," || e.key === " ") {
        e.preventDefault();
        processEmails(inputValue);
        if (inputValue.trim() && !emails.includes(inputValue.trim())) {
          setEmails([...emails, inputValue.trim()]);
          setInputValue("");
        }
      }

      // Backspace to remove the last tag when the input is empty
      if (e.key === "Backspace" && inputValue === "" && emails.length > 0) {
        e.preventDefault();
        setEmails(emails.slice(0, -1));
      }
    } catch (err) {}
  };

  const handleRemoveTag = (index: number) => {
    setEmails(emails.filter((_, i) => i !== index));
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    if (typeof pastedText !== "string" || !pastedText.trim()) return;
    processEmails(pastedText);
  };

  const processEmails = (text: string) => {
    if (!text.trim()) return;

    // Split emails by commas, spaces, or new lines
    const emailList = text
      .split(/[\s,]+/)
      .map((email) => email.trim())
      .filter((email) => email !== "");

    // Email
    // if (emails.length + emailList.length > 50) {
    //   setInvalidEmails([
    //     ...invalidEmails,
    //     "You can enter a maximum of 50 emails",
    //   ]);
    //   return;
    // }

    // Email limit check (if adding emails exceeds the limit)
    if (emails.length + emailList.length > 50) {
      setInvalidEmails(["You can enter a maximum of 50 emails"]);
      return;
    } else {
      // Clear the error message if emails drop below the limit
      setInvalidEmails([]);
    }

    const validEmails = emailList.filter((email) => mailRegex.test(email));
    const invalidList = emailList.filter((email) => !mailRegex.test(email));

    // setEmails([...emails, ...validEmails]);
    // setInvalidEmails([...invalidEmails, ...invalidList]);

    // Add valid emails and filter out duplicates
    setEmails((prevEmails) => {
      const newEmails = [...prevEmails, ...validEmails];
      return newEmails.slice(0, 50); // Ensure no more than 50 emails
    });

    // Set the invalid emails state
    setInvalidEmails((prevInvalidEmails) => [
      ...prevInvalidEmails,
      ...invalidList,
    ]);

    // Update form field value to avoid validation errors
    form.setFieldsValue({ emails: [...emails, ...validEmails] });

    // Revalidate the field to remove any previous validation messages
    form.validateFields(["emails"]);

    // Filter out already added emails
    const newEmails = emailList.filter(
      (email) => !emails.includes(email) && mailRegex.test(email)
    );

    if (newEmails.length > 0) {
      setEmails([...emails, ...newEmails]);
    }

    setInputValue(""); // Clear input field
  };

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      handleOpen({
        type: "SUCCESS",
      });
    }
  }, [handleOpen, isSuccess]);

  useEffect(() => {
    // Check if error is FetchBaseQueryError and has status
    if (
      error &&
      typeof error === "object" &&
      "status" in error &&
      (error as { status?: number }).status === 409
    ) {
      const existEmails =
        error && "data" in error && error.data && typeof error.data === "object"
          ? ((error.data as any)?.data?.usedEmails as string[])
          : [];

      handleOpen({
        type: "EXIST",
        data: existEmails,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  return (
    <CustomModal
      title="Invite Employee"
      {...rest}
      handleClose={handleClose}
      width={535}
      footer={null}
    >
      {open?.type === "SUCCESS" && (
        <InviteSuccessModal
          open={open.open}
          handleClose={() => {
            handleClose();
            handleChildClose();
          }}
        />
      )}
      {open?.type === "EXIST" && (
        <ExistEmailModal
          data={open?.data}
          open={open.open}
          handleClose={() => {
            handleChildClose();
          }}
        />
      )}
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <Form.Item
          name="emails"
          label="Invite single and multiple employees"
          className="mb-[18px]"
          rules={[
            // {
            //   required: true,
            //   message: "Please enter at least one email",
            // },
            {
              validator: async (_: any, value: string | string[]) => {
                if (!value || (Array.isArray(value) && value.length === 0)) {
                  return Promise.reject(
                    new Error("Please enter at least one email")
                  );
                }

                // Ensure value is a string before proceeding
                const emailString = Array.isArray(value)
                  ? value.join(", ")
                  : value;

                // Split input into multiple emails using commas or spaces
                const emailList = emailString
                  .split(/[\s,]+/)
                  .map((email) => email.trim())
                  .filter((email) => email !== "");

                if (emailList.length === 0) {
                  return Promise.reject(
                    new Error("Please enter at least one email")
                  );
                }

                // Validate each email
                const invalidEmails = emailList.filter(
                  (email) => !mailRegex.test(email)
                );

                if (invalidEmails.length > 0) {
                  return Promise.reject(
                    new Error("Please enter a valid email")
                  );
                }

                const invalidLTDEmails = emailList.filter(
                  (email) => !isValidTLD(email)
                );

                if (invalidLTDEmails.length > 0) {
                  return Promise.reject(new Error("Please enter a valid TLD"));
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <div className="border rounded-md border-display-medium p-2.5">
            <div className="flex flex-wrap gap-1">
              {emails.map((tag, index) => (
                <span
                  key={index}
                  className="bg-extra-light text-display-body font-medium px-1.5 py-0.5 rounded-md flex items-center space-x-1.5"
                >
                  <span>{tag}</span>
                  <button
                    onClick={() => handleRemoveTag(index)}
                    className="text-sub-header hover:text-header"
                    type="button"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              rows={4}
              // className="w-full mt-1 focus:ring-0 focus:outline-none text-sub-header border-none placeholder:text-extra-gray text-base resize-none"
              className={`w-full mt-1 focus:ring-0 focus:outline-none border-none placeholder:text-extra-gray text-base resize-none ${
                invalidEmails.length > 0 ? "border border-red-500" : ""
              }`}
              placeholder="Enter email addresses, separated by commas or spaces"
            />
          </div>

          {invalidEmails.length > 0 && (
            <p className="text-red-500 mt-1">{invalidEmails.join(", ")}</p>
          )}
        </Form.Item>

        <Space
          align="center"
          size={12}
          className="w-full px-4 py-1.5 rounded-md text-status-danger-text bg-[#fff4f4] max-h-9"
        >
          <WarningIcon width={24} height={24} />
          <div className="text-sm font-medium">
            Press enter to separate the email addresses
          </div>
        </Space>

        <Space className="w-full mt-6" size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
            disabled={isLoading}
            htmlType="button"
          >
            Cancel
          </Button>
          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
            loading={isLoading}
            disabled={!emails.length}
          >
            Send Invitation
          </Button>
        </Space>
      </Form>
    </CustomModal>
  );
}
